import { useSyncExternalStore } from "react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useInternalAdmin } from "@/hooks/useInternalAdmin";
import { isInternalAdminEmail } from "@/lib/internalAccess";
import {
  getRoleOverride,
  getRoleOverrideServer,
  subscribeRoleOverride,
} from "@/lib/roleOverrideStore";

export type WorkspaceRole = "owner" | "administrator" | "photographer" | "salesperson";

// Shared role sets mirroring the legacy admin capability gating (and the
// server-side authz sets). ADMIN = owner/administrator; UPLOADER adds
// photographer (upload/duplicate); SALES adds salesperson (orders).
export const ADMIN_ROLES: WorkspaceRole[] = ["owner", "administrator"];
export const UPLOADER_ROLES: WorkspaceRole[] = ["owner", "administrator", "photographer"];
export const SALES_ROLES: WorkspaceRole[] = ["owner", "administrator", "salesperson"];

/**
 * Role-based access, mirroring the legacy admin gating. `can(roles)` returns true
 * when the current role is in the allowed set (used to gate tabs/actions/nav exactly
 * like legacy).
 *
 * The current role is the user's REAL role on this workspace (`workspace.role` from
 * GET /organizations), falling back to the MOST restricted role ('photographer') when
 * missing — never 'owner'. This fails closed so a role that fails to load can't gain
 * owner access.
 *
 * Photohawk STAFF (backend `isSuperUser`, or the internal-builder email) get
 * owner-level access on any workspace. NOTE: this is deliberately NOT keyed off
 * `isInternal` (useInternalAdmin) — that flag is the v2-admin *rollout* gate, which is
 * now true for EVERY signed-in user since the general cutover. Keying owner access off
 * it would (and did) hand every real user — photographers included — owner-level UI,
 * while the backend still authorised by their true role. Staff status is the correct
 * signal.
 *
 * Staff (canOverrideRole) may additionally set a "view as role" override (see
 * roleOverrideStore) to TEST gating — it changes only what this hook reports, never
 * real backend access. When active, `role`/`can`/`isAdmin` reflect the overridden
 * role; `realRole` is the un-overridden one.
 */
export const useRole = () => {
  const { workspace, isSuperUser } = useWorkspace();
  const { isInternal, email } = useInternalAdmin();

  // "Staff" = Photohawk's own people, who legitimately get owner access on any
  // workspace. Backend-authoritative isSuperUser first; the internal-builder email is
  // a belt-and-braces fallback (and what also unlocks the testing override below).
  const isStaff = isSuperUser || isInternalAdminEmail(email);

  const realRole: WorkspaceRole = isStaff
    ? "owner"
    : ((workspace?.role as WorkspaceRole) || "photographer");

  // Who may use the "view as role" override: Photohawk staff, and a workspace's own
  // OWNER (so they can preview what each of their team's roles sees, for peace of
  // mind). It's a client-only lens — the backend still authorises by the real token —
  // and it can only ever RESTRICT the UI below the user's real role, never widen it,
  // so letting an owner use it grants no extra access. Everyone else ignores any
  // stored override value entirely.
  const canOverrideRole = isStaff || realRole === "owner";
  const override = useSyncExternalStore(
    subscribeRoleOverride,
    getRoleOverride,
    getRoleOverrideServer,
  );
  const overrideRole = canOverrideRole ? override : null;

  const role: WorkspaceRole = overrideRole || realRole;

  const can = (allowed: WorkspaceRole[]) => allowed.includes(role);
  const isAdmin = role === "owner" || role === "administrator";

  return {
    role,
    can,
    isAdmin,
    isInternal,
    // Photohawk staff (backend isSuperUser, or the internal-builder email). Staff-only UI (e.g. the gallery
    // People tab) keys off this — never the role, so no workspace member ever sees it. The backend still
    // authorises staff-only endpoints by the token (403 for everyone else).
    isStaff,
    // Testing-override surface (used by the RoleTester control).
    realRole,
    overrideActive: !!overrideRole,
    canOverrideRole,
  };
};
